import { LineItemCreate, LineItemDiscount } from "@wingspanhq/wingspan-api-sdk";
import { getRequestDataCustomFields } from "../../shared/components/FormPartialCustomFields";
import { FormDataLineItem } from "../components/InvoicesForm/LineItemsSection/types";
import { LINE_ITEM_HOUR_RATE, LINE_ITEM_UNIT_RATE } from "../constants";
import { timeToQuantity } from "../utils";
import { selectorLineItemTypeFromFormData } from "./selectorLineItemTypeFromFormData";

/**
 * The form keeps a discount's percentage and amount side by side so switching
 * between the two doesn't lose what was typed; only the side `type` points at
 * is sent, and a zero on that side means "no discount".
 */
const lineItemDiscount = (
  discount: FormDataLineItem["discount"]
): LineItemDiscount | undefined => {
  if (discount?.type === "percentage" && discount.percentage) {
    return {
      percentage: discount.percentage,
      description: discount.description
    };
  }

  if (discount?.type === "amount" && discount.amount) {
    return { amount: discount.amount, description: discount.description };
  }

  return undefined;
};

/**
 * Builds the v3 `LineItemCreate` bodies for every line item the form is
 * submitting — used by invoice create/update and payable create/update alike,
 * since v3 takes the same line-item shape on all four.
 *
 * Line items flagged for removal drop out, so the result is not index-aligned
 * with the form's array.
 *
 * The per-line-item QuickBooks item/expense mapping the form still collects
 * (`FormDataLineItem.integration`) is deliberately not carried: v3 models it as
 * `LineItemCreate.accounting`, a different shape, and the create endpoint
 * rejects it with `422 "accounting not yet supported"`.
 */
export const selectorLineItemsCreate = (
  formLineItems: FormDataLineItem[]
): LineItemCreate[] =>
  formLineItems.flatMap((formLineItem): LineItemCreate[] => {
    if (formLineItem.remove) {
      return [];
    }

    const lineItem: LineItemCreate = {
      description: formLineItem.description,
      lineItemType: selectorLineItemTypeFromFormData(formLineItem),
      discount: lineItemDiscount(formLineItem.discount),
      metadata: formLineItem.customFields
        ? (getRequestDataCustomFields(formLineItem.customFields) as {
            [key: string]: string;
          })
        : undefined
    };

    // A fixed line item prices as a lump sum. Every other rate prices as
    // unit × quantity and leaves the total for the API to derive.
    if (formLineItem.rate === "fixed") {
      return [{ ...lineItem, totalCost: formLineItem.totalCost }];
    }

    return [
      {
        ...lineItem,
        unit:
          formLineItem.rate === "hourly"
            ? LINE_ITEM_HOUR_RATE
            : formLineItem.rate === "quantity"
              ? LINE_ITEM_UNIT_RATE
              : formLineItem.customUnit,
        unitCost: formLineItem.costPerUnit,
        // An hourly line item splits its duration across two inputs; v3 takes a
        // single fractional-hour quantity.
        quantity:
          formLineItem.rate === "hourly"
            ? timeToQuantity(
                formLineItem.quantity || 0,
                formLineItem.minutes || 0
              )
            : formLineItem.quantity
      }
    ];
  });
